/* eslint-disable arrow-body-style */
import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {Project, ProjectProps} from "../models/project";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {ProjectProcess} from "../models/project-process";
import {Difference} from "../models/difference";
import {JournalStore} from "./journal-store-mobx";
import {ProjectProcessStore} from "./project-process-store-mobx";
import {ProjectPerimeterStore} from "./project-perimeter-store-mobx";
import {ProjectType} from "./auftragsverwaltung-mobx";
import {PartialOrderStore} from "./partial-order-store-mobx";
import {PartialOrder} from "../models/partial-order";
import {ContractorFilter} from "../filter/contractor-filter";
import {ProjectProcessFilter} from "../filter/project-process-filter";
import {ProjectTypeFilter} from "../filter/project-type-filter";
import {ProjectMunicipalityFilter} from "../filter/project-municipality-filter";
import {ProjectMunicipalityStore} from "./project-municipality-store-mobx";
import {DocumentStore} from "./document-store-mobx";
import {ProjectPerimeter} from "../models/project-perimeter";

export type PartialOderStoreType = "partial-order-gk" | "partial-order-ghk" | "partial-order-risk";

export interface ProjectStoreProps {
    layerConfig: ApiLayerConfiguration;
    journalStore: JournalStore;
    processStore: ProjectProcessStore;
    perimeterStore: ProjectPerimeterStore;
    projectMunicipalityStore: ProjectMunicipalityStore;
    partialOrderStores: Map<PartialOderStoreType, PartialOrderStore>;
    documentStore: DocumentStore;
}

export class ProjectStore extends AbstractStore {

    journalStore: JournalStore | undefined;
    processStore: ProjectProcessStore;
    perimeterStore: ProjectPerimeterStore;
    projectMunicipalityStore: ProjectMunicipalityStore;
    partialOrderStores: Map<PartialOderStoreType, PartialOrderStore>;
    documentStore: DocumentStore;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, Project>();

    /**
     * Flag to hide closed projects
     */
    hideClosedProjects: boolean = true;

    constructor(props: ProjectStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.journalStore = props.journalStore;
        this.processStore = props.processStore;
        this.perimeterStore = props.perimeterStore;
        this.partialOrderStores = props.partialOrderStores;
        this.documentStore = props.documentStore;
        this.projectMunicipalityStore = props.projectMunicipalityStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            filters: observable,
            hideClosedProjects: observable,
            sort: observable,
            visibleItems: computed,
            visibleItemsGk: computed,
            visibleItemsRisk: computed,

            addItem: action,
            removeItem: action,
            clearItems: action,
            clearFilters: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Project[] {
        let filtered = [...this.items.values()].filter((p: Project) => {
            if (this.hideClosedProjects) {
                // only show active projects
                return p.statusId === 1;
            }
            return true;
        });
        // apply filters
        if (this.filters && this.filters.length > 0) {
            filtered = ContractorFilter.apply(filtered, this.filters);
            filtered = ProjectProcessFilter.apply(filtered, this.filters);
            filtered = ProjectTypeFilter.apply(filtered, this.filters);
            filtered = ProjectMunicipalityFilter.apply(filtered, this.filters);
        }

        const searchTermLower = this.searchTerm?.toLowerCase().trim();

        let searchedProjects: Project[] | undefined;
        if (searchTermLower && searchTermLower.length > 0) {
            searchedProjects = filtered.filter((p) => {
                let idMatch: boolean = false;
                if (p.id && this.searchTerm && !isNaN(+this.searchTerm)) {
                    idMatch = p.id.toString().toLowerCase().includes(this.searchTerm);
                }
                const nameMatch = p.name?.toLowerCase().includes(searchTermLower);
                return idMatch || nameMatch;
            });
        }
        else {
            // no search filter
            searchedProjects = filtered;
        }

        // sort
        if (this.sort) {
            const [attr, direction] = this.sort.split("-");
            let domain: __esri.CodedValueDomain | undefined;
            if (this.domainsByField.has(attr)) {
                domain = this.domainsByField.get(attr);
            }
            searchedProjects.sort(this.sortBy(attr, direction, domain));
        } else {
            // default sort
            //const typeDomain = this.domainsByField.get("typeId");
            //searchedProjects.sort(this.sortBy("typeId", "desc", typeDomain));
            searchedProjects.sort(Project.sortByDate);
        }
        return [ ...searchedProjects];
    }

    get visibleItemsGk(): Project[] | null {
        // apply filters
        let filtered = [...this.items.values()].filter((p: Project) => {
            if (this.hideClosedProjects) {
                // only show active projects
                return (p.typeId === 1 || p.typeId === 2 || p.typeId === 3) && p.statusId === 1;
            }
            return (p.typeId === 1 || p.typeId === 2 || p.typeId === 3);
        });
        // apply filters
        if (this.filters && this.filters.length > 0) {
            filtered = ContractorFilter.apply(filtered, this.filters);
            filtered = ProjectProcessFilter.apply(filtered, this.filters);
            filtered = ProjectTypeFilter.apply(filtered, this.filters);
            filtered = ProjectMunicipalityFilter.apply(filtered, this.filters);
        }

        const searchTermLower = this.searchTerm?.toLowerCase().trim();

        let searchedProjects: Project[] | undefined;
        if (searchTermLower && searchTermLower.length > 0) {
            searchedProjects = filtered.filter((p) => {
                let idMatch: boolean = false;
                if (p.id && this.searchTerm && !isNaN(+this.searchTerm)) {
                    idMatch = p.id.toString().toLowerCase().includes(this.searchTerm);
                }
                const uuidMatch = p.uuid?.toLowerCase().includes(searchTermLower);
                const nameMatch = p.name?.toLowerCase().includes(searchTermLower);
                return idMatch || uuidMatch || nameMatch;
            });
        }
        else {
            // no search filter
            searchedProjects = filtered;
        }

        // sort
        if (this.sort) {
            const [attr, direction] = this.sort.split("-");
            let domain: __esri.CodedValueDomain | undefined;
            if (this.domainsByField.has(attr)) {
                domain = this.domainsByField.get(attr);
            }
            searchedProjects.sort(this.sortBy(attr, direction, domain));
        } else {
            // default sort
            //const typeDomain = this.domainsByField.get("typeId");
            //searchedProjects.sort(this.sortBy("typeId", "desc", typeDomain));
            searchedProjects.sort(Project.sortByDate);
        }
        return [ ...searchedProjects];
    }

    /**
     * Currently visible Risikoübersicht projects by applied filters
     */
    get visibleItemsRisk(): Project[] | null {
        // apply filters
        let filtered = [...this.items.values()].filter((p: Project) => {
            if (this.hideClosedProjects) {
                // only show active projects
                return p.typeId === 4 && p.statusId === 1;
            }
            return p.typeId === 4;
        });
        // apply filters
        if (this.filters && this.filters.length > 0) {
            filtered = ContractorFilter.apply(filtered, this.filters);
            filtered = ProjectProcessFilter.apply(filtered, this.filters);
            filtered = ProjectTypeFilter.apply(filtered, this.filters);
            filtered = ProjectMunicipalityFilter.apply(filtered, this.filters);
        }

        const searchTermLower = this.searchTerm?.toLowerCase().trim();

        let searchedProjects: Project[] | undefined;
        if (searchTermLower && searchTermLower.length > 0) {
            searchedProjects = filtered.filter((p) => {
                let idMatch: boolean = false;
                if (p.id && this.searchTerm && !isNaN(+this.searchTerm)) {
                    idMatch = p.id.toString().toLowerCase().includes(this.searchTerm);
                }
                const uuidMatch = p.uuid?.toLowerCase().includes(searchTermLower);
                const nameMatch = p.name?.toLowerCase().includes(searchTermLower);
                return idMatch || uuidMatch || nameMatch;
            });
        }
        else {
            // no search filter
            searchedProjects = filtered;
        }

        // sort
        if (this.sort) {
            const [attr, direction] = this.sort.split("-");
            let domain: __esri.CodedValueDomain | undefined;
            if (this.domainsByField.has(attr)) {
                domain = this.domainsByField.get(attr);
            }
            searchedProjects.sort(this.sortBy(attr, direction, domain));
        } else {
            // default sort
            //const typeDomain = this.domainsByField.get("typeId");
            //searchedProjects.sort(this.sortBy("typeId", "desc", typeDomain));
            searchedProjects.sort(Project.sortByDate);
        }
        return [ ...searchedProjects];
    }

    /**
     * Override to use project id as key
     * Add one or multiple items to the store
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: Project | Map<string, Project>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(String(item.id))) {
                this.items.delete(String(item.id));
            }
            this.items.set(String(item.id), item);
        }
    }

    /**
     * Override to use project id as key
     * Remove an item from store
     * @param item
     */
    removeItem(item: Project): void {
        if (this.items.has(String(item.id))) {
            this.items.delete(String(item.id));
        }
    }

    /**
     * Override to use project id as key
     * Get an item by GUID
     * @param projectId
     */
    getItem(projectId: string): Project | null {
        const item = this.items.get(projectId);
        return item || null;
    }

    loadItem(projectId: number): Promise<Project | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            if (!projectId) {
                throw new Error("project guid undefined when loading project item");
            }

            // !! do not use loadItems here. Then it wouldn't be possible to sync the
            // observed properties without swapping the whole items!!
            this.queryFeatures([projectId]).then((projects: Project[]) => {
                if (projects[0] && projects[0].id === Number(projectId)) {
                    const project = projects[0];
                    const localProject = this.items.get(String(project.id));
                    if (localProject) {
                        // return loadProject as long as merging process is running. project can be displayed with
                        // indicator that synching is in process
                        // TODO: merge project with already loaded project
                    }
                    // load process for project
                    this.processStore.loadItem(project.id).then((process) => {
                        // TODO: load processes before and get from local store
                        if (!process) {
                            console.error(`No process found for project ${project.id}.`);
                        }
                        project.process = process;
                    }).catch((error) => {
                        console.error(error);
                        this.setDataStatus("error");
                        throw new Error("Error loading project process");
                    });
                    this.addItem(project);
                    this.setSelectedItem(project);
                    this.setDataStatus("loaded");

                    resolve(project);
                } else {
                    reject(`Project not found with id ${projectId}`);
                    throw new Error("Project not found");
                }
            }).catch((error) => {
                console.error(error);
                this.setDataStatus("error");
                throw new Error("Error loading project");
            });
        });
    }

    /**
     * Request all projects from server and store in local store
     *
     * @param ids (Optional) IDs of projects
     * @param contractorIds
     */
    loadItems(ids?: number[], contractorIds?: number[]): Promise<Project[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // first load all processes, then load projects and pass processes to projects, so the process is more
                // easily accessible
                this.processStore.loadItems(ids).then((processes) => {
                    if (!processes) {
                        reject();
                        throw new Error("Could not load processes");
                    }

                    const processesMap = new Map<string, ProjectProcess>();
                    processes.forEach((proc: ProjectProcess) => {
                        processesMap.set(String(proc.projectId), proc);
                    });

                    this.queryFeatures(ids, contractorIds).then((projects: Project[]) => {
                        if (projects) {
                            // do only save to mobx when not requested with specified ids or contacts
                            const map = new Map<string, Project>();
                            projects.forEach((p: Project) => {
                                if (!p.id) {
                                    console.error("Project found without ID.");
                                    return;
                                }
                                const process = processesMap.get(String(p.id));
                                if (!process) {
                                    console.error(`No process found for project ${p.id}`);
                                }
                                p.process = process;
                                map.set(String(p.id), p);
                            });
                            // TODO: find a better way to change between plannings
                            this.clearItems();  // clear loaded items. could belong to another planning
                            this.addItem(map);
                            this.setDataStatus("loaded");

                            resolve(projects);
                        } else {
                            this.setDataStatus("error");
                            reject();
                            throw new Error("Error requesting projects");
                        }
                    });
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param user
     */
    newItem(user: string): Promise<Project | null> {
        return new Promise((resolve) => {
            // generate random project id
            const randomIds = new Uint8Array(1);
            self.crypto.getRandomValues(randomIds);

            const project = new Project({
                id: randomIds[0],
                guid: crypto.randomUUID(),
                statusId: 1,  // aktiv TODO: remove state from project
                creator: user,
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // TODO: find out why the feature template prototype does not return attributes
                project.feature = this.createFeature(null, project);

                // workaround: add missing prototype attributes
                Project.prototypeAttributes.forEach((attrName) => {
                    if (!project.feature.attributes[attrName]) {
                        project.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(project);
                this.setSelectedItem(project);

                resolve(project);
            });
        });
    }

    /**
     * Save project to the server
     *
     * @param project
     * @param type
     * @param updated
     * @param user
     */
    save(project: Project | null,
         type: ProjectType,
         updated: any,
         user: string): Promise<Project | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project?.guid) {
                    reject("Project GUID undefined when saving edits.");
                    return;
                }

                if (project.isNewRecord) {
                    // New project --> create feature
                    project.feature = this.createFeature(null, project);

                    // create process if project is saved the first time
                    this.processStore.createProcess(type, project.id, user);

                    // log project creation in journal
                    const nameDiff = new Difference({p: "name", n: updated["name"] || project.name || "-"});
                    const idDiff = new Difference({p: "id", n: project.id?.toString()});
                    this.journalStore?.journalizeItem(
                        project,
                        "project",
                        "add",
                        user,
                        [idDiff, nameDiff],
                        "Neues Projekt"
                    );
                }

                if(!project.isNewRecord) {
                    // log changes to journal
                    const diff = this.compareAttributes(project, updated, Project.comparableAttributes);
                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        project,
                        "project",
                        "update",
                        user,
                        diff,
                        "Projekt geändert"
                    );
                }

                const feature = project?.feature;
                if (!feature) {
                    reject(`Feature undefined for project ${project.id}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = Project.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            throw new Error(`Database field not found for project ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = project.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(project, attrs);
                    if (project.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        project.feature.attributes["OBJECTID"] = objectId;
                    }

                    // attach process when new record
                    if (mode === "add") {
                        this.processStore.loadItem(project.id).then((proc) => {
                            project.process = proc;
                            resolve(project);
                        });
                    }
                    resolve(project);
                }, (error) => {
                    console.error("Error saving project", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete a project
     *
     * @param project
     * @param user
     */
    delete(project: Project | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project?.guid) {
                    reject("Project GUID undefined when saving edits.");
                    return;
                }

                if (project.isNewRecord) {
                    reject("This project is not saved. Does not have to be deleted.");
                    return;
                }

                // do not allow deleting when process is further than a specified step
                if (project.process && project.process.statusConfig &&
                    !project.process.statusConfig.canChangeProject) {

                    reject("This project cannot be deleted. Process is running.");
                    return;
                }

                const feature = project?.feature;
                if (!feature) {
                    reject(`Feature undefined for project ${project.id}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {

                    this.resetSelectedItem();
                    this.removeItem(project);

                    // log deletion in journal
                    const nameDiff = new Difference({p: "name", o: project.name || "-"});
                    const idDiff = new Difference({p: "id", o: project.id?.toString()});
                    this.journalStore?.journalizeItem(
                        project,
                        "project",
                        "delete",
                        user,
                        [idDiff, nameDiff],
                        "Projekt gelöscht"
                    );

                    resolve(objectId);

                    // delete process
                    if (project.process) {
                        this.processStore.delete(project.process, user);
                    }

                    // delete partial orders
                    if (project.typeId === 4) {
                        this.deletePartialOrders("partial-order-risk", project, user);
                    }
                    else {
                        this.deletePartialOrders("partial-order-gk", project, user);
                        this.deletePartialOrders("partial-order-ghk", project, user);
                    }
                    // delete project perimeter
                    this.deletePerimeters(project, user);
                });
            });
        });
    }

    queryFeatures(projectIds?: number[], contractorIds?: number[]): Promise<Project[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                // load all features if no project ID is specified
                if (projectIds !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND PROJ_ID IN (${projectIds.join("','")})`;
                }
                if (contractorIds !== undefined) {
                    queryParams.where += ` AND AUFTRAGNEHMER_ID IN (${contractorIds.join(",")})`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const projects = this.parseQueryResult(results);
                        resolve(projects);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying project + ${error}`);
                });
            });
        });
    }

    requestIds(searchFilter: string): Promise<number[] | null> {
        return new Promise((resolve) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();
                queryParams.returnGeometry = false;

                if (searchFilter !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND CAST(PROJ_ID as VARCHAR(20)) LIKE '${searchFilter}%'`;
                }

                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const projects = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        if (projects) {
                            // @ts-expect-error Null values are filtered!
                            const ids: number[] = projects.map((p) => p.id).filter((id) => id);
                            resolve(ids);
                            return;
                        }
                    }
                    resolve(null);
                });
            });
        });
    }

    maxProjectId(start: number): Promise<number | null> {
        return new Promise((resolve) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();
                queryParams.returnGeometry = false;
                queryParams.where += ` AND CAST(PROJ_ID as VARCHAR(20)) LIKE '${start}%'`;

                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const projects = this.parseQueryResult(results);
                        if (projects) {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            const ids: number[] = projects.map((p) => p.id).filter((id) => {
                                return id !== null && id !== undefined;
                            });
                            if (!ids || ids.length === 0) {
                                resolve(null);
                                return;
                            }
                            const maxId = Math.max(...ids);
                            resolve(maxId);
                            return;
                        }
                    }
                    resolve(null);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): Project[] {

        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const projects: Project[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Project);
            const a = new Project(json as ProjectProps);
            a.feature = feature;
            projects.push(a);
        });
        return projects;
    }

    private deletePartialOrders(type: PartialOderStoreType, project: Project, user: string): void {
        if (!project?.id) {
            return;
        }
        // reload partial orders to be sure to have current items
        const store = this.partialOrderStores.get(type);
        if (!store) {
            throw new Error("Store not found for partial order deleting partial orders on project.");
        }

        store?.loadItems(project.id).then((partialOrders: PartialOrder[]) => {
            partialOrders.forEach((po: PartialOrder) => {
                store?.delete(project, po, user);
            });
        });
    }

    private deletePerimeters(project: Project, user: string): void {
        if (!project?.id) {
            return;
        }
        // reload perimeters to be sure to have current items
        if (!this.perimeterStore) {
            throw new Error("Perimeter store not found when deleting perimeters on project.");
        }

        this.perimeterStore?.loadItems(project.id).then((perimeters: ProjectPerimeter[]) => {
            // unsure not to delete all perimeters in case of failure
            if (perimeters.length > 3) {
                console.warn("Deleting too many perimeters at once. Must be an error");
                return;
            }
            perimeters.forEach((p: ProjectPerimeter) => {
                this.perimeterStore?.delete(p, user);
            });
        });
    }

}
